<template>
  <fts-popper append-to-body placement="bottom-start" content-class="listing-designer-dropdown">
    <template #content>
      <!-- Top Bar -->
      <div class="top-bar">
        <div class="module-form-emoji-wrapper">
          <fts-emoji placement="bottom" class="module-create-emoji" v-model="emoji" :toggle-size="16" :width="400" />
        </div>

        <!-- Add editable text next to emoji -->
        <input type="text" class="editable-text" v-model="moduleName" placeholder="" ref="searchInput" />
      </div>

      <div class="section">
        <div class="title" style="display: flex; align-items: center; justify-content: space-between;">
          <!-- Left Side -->
          <div class="title-left" style="display: flex; align-items: center; gap: 6px;">
            <i class="fts-icon-magic-lamp" style="color: var(--fts-ai)" />
            <span style="font-weight: bold; color: var(--fts-ai);">Gney AI</span>
            <span>— Wish It, Done.</span>
            <fts-ai-informations-dropdown :informations="informations" :initial-selected="appToShow"
              @select="(ids) => onSelectAiOption(ids)">
              <template #toggle="{ open }">
                <font-awesome-icon :icon="['fas', 'cog']" />
                <font-awesome-icon :icon="['fas', 'check']" :width="10" v-if="appToShow" />
              </template>
            </fts-ai-informations-dropdown>
          </div>

          <!-- Right Side: Toggle -->
          <fts-toggle-switch color="primary" v-model="aiEnabled" @change="onAiEnabledToggle" mat-class :size="16" />
        </div>
      </div>

      <!-- Card Design Section -->
      <div class="section">
        <div class="card-section-wrapper">
          <div class="card-column">

            <div v-for="(row, rowIndex) in rows" :key="rowIndex">
              <fts-add-row-dropdown :invalid-options="getInvalidItems(rowIndex)" :informations="informations"
                :activities="activities" :initial-selected="rows[rowIndex].items"
                @select="(ids) => onSelectOption(ids, rowIndex)" ref="addRowDropdown">
                <template #toggle="{ open }">
                  <div class="card-row"
                    :class="{ 'default-card': isDefaultCardRow(row, rowIndex) || isImageCardRow(row, rowIndex) }">
                    <div @click.stop>
                      <fts-designer-row-menu-dropdown :index="rowIndex" :totalLength="rows.length"
                        @select="(operation) => onRowRearrange(rowIndex, operation)">
                        <template #toggle="{ open }">
                          <font-awesome-icon class="drag-icon" :icon="['fas', 'bars']" :width="10" />
                        </template>
                      </fts-designer-row-menu-dropdown>
                    </div>

                    <div v-for="(cell, i) in row.items" :key="i" class="row-cell"
                      :class="isDefaultCardRow(row, rowIndex) || isImageCardRow(row, rowIndex) ? 'align-center' : getCellAlignClass(row.length, i)">
                      <div v-if="isDefaultCardRow(row, rowIndex)">Default Card</div>
                      <div v-else-if="isImageCardRow(row, rowIndex)">
                        <font-awesome-icon :icon="['fas', 'image']" />
                      </div>
                      <div v-else-if="cell.type === 'information'" :style="{
                        fontSize: cell.size === 'S' ? '8px' : (cell.size === 'L' ? '16px' : ''),
                        fontWeight: cell.style === 'l' ? 100 : (cell.style === 'b' ? 800 : '')
                      }">
                        {{ getInformationName(cell) }}
                      </div>
                      <div v-else-if="cell.type === 'activity'">
                        <div class="activity-cell">{{ getActivityName(cell) }}</div>
                      </div>
                    </div>
                  </div>
                </template>
              </fts-add-row-dropdown>
            </div>

            <div class="add-row-wrapper">
              <fts-add-row-dropdown :invalid-options="getInvalidItems()" :informations="informations"
                :activities="activities" @select="(ids) => onSelectOption(ids, null)" ref="addRowDropdown">
                <template #toggle="{ open }">
                  <button class="add-row-button">
                    <font-awesome-icon :icon="['fas', 'plus']" /> Row
                  </button>
                </template>
              </fts-add-row-dropdown>
            </div>

          </div>
        </div>
      </div>

      <!-- Settings Section -->
      <div class="section">
        <fts-default-action-dropdown :activities="activities" :default-activities="defaultActivities"
          :initialSelected="action" @select="onSelectAction">
          <template #toggle="{ open }">
            <div class="settings-item">
              <font-awesome-icon :icon="['fas', 'arrow-pointer']" />
              <span>Default Action</span>
              <button class="edit-button">{{ getActionName(action) }} <font-awesome-icon
                  :icon="['fas', 'chevron-right']" width="10px" /></button>
            </div>
          </template>
        </fts-default-action-dropdown>
      </div>
    </template>

    <div class="listing-designer-toggle" slot="reference">
      <slot>
        <div :style="getToggleStyle"></div>
      </slot>
    </div>
  </fts-popper>
</template>

<script>
import FTSPopper from './FTSPopperNew';
import FTSEmoji from '../elements/FTSEmoji';
import FTSToggleSwitch from '../elements/FTSToggleSwitch';
import FTSDropdownSelect from "../elements/FTSDropdownSelect";
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSAddRowDropdown from './FTSAddRowDropdown';
import FTSDefaultActionDropdown from './FTSDefaultActionDropdown';
import FTSAiInformationsDropdown from './FTSAiInformationsDropdown';
import FTSDesignerRowMenuDropdown from './FTSDesignerRowMenuDropdown';

export default {
  name: 'ListingDesigner',
  components: {
    'fts-popper': FTSPopper,
    'fts-emoji': FTSEmoji,
    'fts-toggle-switch': FTSToggleSwitch,
    "fts-dropdown-select": FTSDropdownSelect,
    'fts-label-group': FTSLabelGroup,
    'fts-add-row-dropdown': FTSAddRowDropdown,
    'fts-default-action-dropdown': FTSDefaultActionDropdown,
    'fts-ai-informations-dropdown': FTSAiInformationsDropdown,
    'fts-designer-row-menu-dropdown': FTSDesignerRowMenuDropdown,
  },
  props: {
  },
  model: {
  },
  methods: {
    onRowRearrange(index, operation) {
      if (index < 0 || index >= this.rows.length) return;

      const row = this.rows[index];
      this.rows.splice(index, 1);

      switch (operation) {
        case 'moveTop':
          this.rows.unshift(row);
          break;
        case 'moveBottom':
          this.rows.push(row);
          break;
        case 'moveUp':
          this.rows.splice(Math.max(index - 1, 0), 0, row);
          break;
        case 'moveDown':
          this.rows.splice(Math.min(index + 1, this.rows.length), 0, row);
          break;
        case 'removeRow':
          break;
        default:
          this.rows.splice(index, 0, row);
      }
    },
    onAiEnabledToggle() {
      if (this.aiEnabled && this.rows.length > 0) {
        this.rows = [];
      }
    },
    onSelectAiOption(selectedIds) {
      if (selectedIds.length > 0) {
        this.appToShow = selectedIds;
      } else {
        this.appToShow = null;
      }
    },
    onSelectAction(action) {
      this.action = action;
    },
    onSelectOption(selectedIds, index) {
      if (this.aiEnabled && selectedIds.length > 0) this.aiEnabled = false;
      if (index === null && selectedIds.length > 0) {
        const newRow = { items: selectedIds };
        this.rows.push(newRow);
      }
      else if (selectedIds.length === 0 && index !== null) {
        this.rows.splice(index, 1);
      }
      else if (index !== null) {
        this.rows.splice(index, 1, {
          items: selectedIds,
        });
      }
    },
    isDefaultCardRow(row, index) {
      return (
        Array.isArray(row.items) &&
        row.items.length === 1
        && row.items[0].id === 'defaultCard'
      );
    },
    isImageCardRow(row, index) {
      return this.informations.find(x => x.id === row.items[0].id).type === 10;
    },
    getCellAlignClass(cellCount, index) {
      if (cellCount === 1) {
        return 'align-left';
      }
      if (cellCount === 2) {
        return index === 0 ? 'align-left' : 'align-right';
      }
      if (cellCount === 3) {
        if (index === 0 || index === 1) return 'align-left';
        return 'align-right';
      }
      return '';
    },
    getInvalidItems(i) {
      const otherItems = this.rows
        .filter((_, index) => index !== i)
        .flatMap(row => row.items);
      return otherItems;
    }
  },
  computed: {
    getActionName() {
      return (id) => {
        let activity = this.defaultActivities.find(x => x.id === id);
        if (!activity) {
          activity = this.activities.find(x => x.id === id);
        }
        return activity ? activity.name : '';
      }
    },
    getInformationName() {
      return (item) => {
        return this.informations.find(x => x.id === item.id).displayName;
      };
    },
    getActivityName() {
      return (item) => {
        return this.activities.find(x => x.id === item.id).name;
      };
    },

  },
  data() {
    return {
      emoji: null,
      moduleName: 'a',
      aiEnabled: true,
      appToShow: null,
      rows: [],
      informations: [
        {
          "id": "KypTX",
          "fieldName": "yDFiI",
          "displayName": "Text",
          "type": 0
        },
        {
          "id": "KyRfG",
          "fieldName": "ZWlLf",
          "displayName": "Yes No",
          "type": 1
        },
        {
          "id": "ljpQJ",
          "fieldName": "OEGEg",
          "displayName": "Integer",
          "type": 2
        },
        {
          "id": "xbkST",
          "fieldName": "jrzUv",
          "displayName": "Number",
          "type": 3
        },
        {
          "id": "vnnJs",
          "fieldName": "YwIrm",
          "displayName": "Money",
          "type": 4
        },
        {
          "id": "dwRzG",
          "fieldName": "wTGSG",
          "displayName": "Date",
          "type": 5
        },
        {
          "id": "GfmPi",
          "fieldName": "jPdcl",
          "displayName": "Location",
          "type": 9
        },
        {
          "id": "WXHvy",
          "fieldName": "XvJBx",
          "displayName": "Image",
          "type": 10
        },
        {
          "id": "vMOPC",
          "fieldName": "ExFFg",
          "displayName": "File",
          "type": 12
        },
        {
          "id": "ordIJ",
          "fieldName": "NNqDc",
          "displayName": "Email",
          "type": 25
        },
        {
          "id": "GSkBV",
          "fieldName": "dmrzh",
          "displayName": "Phone Number",
          "type": 26
        },
        {
          "id": "PjqtN",
          "fieldName": "rfWsx",
          "displayName": "Select",
          "type": 27,
          "optionList": [
            {
              "id": "c3463894-eb3f-4e5d-aead-54c8ae80447c",
              "name": "Option 3",
              "color": null
            },
            {
              "id": "4f834abf-83e2-4d64-9f27-1f85f8cea59c",
              "name": "Option 2",
              "color": null
            },
            {
              "id": "71305917-87b6-4be8-8cd0-284e19bb088e",
              "name": "Option 1",
              "color": "#f9c5d7"
            }
          ]
        },
        {
          "id": "FMqWR",
          "fieldName": "aVtfT",
          "displayName": "Signature",
          "type": 29
        },
        {
          "id": "IbamH",
          "fieldName": "VtCvA",
          "displayName": "URL",
          "type": 33
        },
        {
          "id": "WhKPL",
          "fieldName": "XsDfK",
          "displayName": "Multi Text",
          "type": 16
        },
        {
          "id": "JxmeM",
          "fieldName": "huMjX",
          "displayName": "Date Time",
          "type": 6
        },
        {
          "id": "qCTBc",
          "fieldName": "jGTIU",
          "displayName": "Images",
          "type": 11
        },
        {
          "id": "rWBOd",
          "fieldName": "vuwNf",
          "displayName": "Files",
          "type": 13
        },
        {
          "id": "ifsDe",
          "fieldName": "cZMrT",
          "displayName": "Multi Select",
          "type": 28,
          "optionList": [
            {
              "id": "a8bd80c3-ee51-4ea2-a68f-627944edeafc",
              "name": "Option 1",
              "color": null
            },
            {
              "id": "dbd5ff37-834d-4601-98db-3bdb666fba8b",
              "name": "Option 2",
              "color": null
            },
            {
              "id": "edf8c8d8-13c1-4053-b238-f072426efe49",
              "name": "Option 3",
              "color": null
            }
          ]
        },
        {
          "id": "trZKJ",
          "fieldName": "TeMzv",
          "displayName": "Text - Edited 2",
          "type": 0
        },
        {
          "id": "mHXsA",
          "fieldName": "XEJUD",
          "displayName": "Age",
          "type": 2
        },
        {
          "id": "BaUFz",
          "fieldName": "JOSPp",
          "displayName": "Text 1",
          "type": 0
        },
        // system informations
        {
          'id': 'documentId',
          'displayName': "ID",
          'system': true,
          'fieldName': "documentId",
          'type': 0,
        },
        {
          'id': 'state',
          'displayName': "State",
          'system': true,
          'fieldName': "state",
          'type': "state",
        },
        {
          'id': 'assignTo',
          'displayName': "Assign To",
          'system': true,
          'fieldName': "assignTo",
          'type': 20,
        },
        {
          'id': 'createdBy',
          'displayName': 'Created By',
          'system': true,
          'fieldName': "createdBy",
          'type': 20,
        },
        {
          'id': 'createdDate',
          'displayName': "Created Date",
          'system': true,
          'fieldName': "createdDate",
          'type': 5,
        },
        {
          'id': 'lastChangeBy',
          'displayName': "Last Change By",
          'system': true,
          'fieldName': "lastChangeBy",
          'type': 20,
        },
        {
          'id': 'lastChangeDate',
          'displayName': "Last Change Date",
          'system': true,
          'fieldName': "lastChangeDate",
          'type': 5,
        },
        {
          'id': "username",
          'displayName': "Username",
          'system': true,
          'fieldName': "username",
          'type': 19,
        },
      ],
      defaultActivities: [{ 'id': 'view', 'name': 'View', 'fa-icon': 'file' }, { 'id': 'edit', 'name': 'Edit', 'fa-icon': 'pen-to-square' },],
      activities: [{ "id": "69b5071d-5f75-48db-8373-7ce670ecc383", "name": "Activity B", "design": { "rows": [{ "index": "2e5af3a9-ab4f-48e7-9c20-96bffcc4df8d", "items": [{ "id": "KypTX" }] }, { "index": "fa892100-b106-4d6a-822c-68ba20115825", "items": [{ "id": "KyRfG" }] }, { "index": "173dc18a-2957-435a-bb9c-22b0b13e2fcf", "items": [{ "id": "ljpQJ" }] }, { "index": "14eaffc4-86d8-4fe5-ac01-bf725a5d68a9", "items": [{ "id": "xbkST" }] }, { "index": "e05222b8-543f-4fb1-b3d3-e4f94b350cb4", "items": [{ "id": "vnnJs" }] }, { "index": "8c10a128-f148-48c0-a18f-fb92a90ee381", "items": [{ "id": "dwRzG" }] }, { "index": "4fbb5fc3-9b2a-46d6-b225-212119e46ccf", "items": [{ "id": "GfmPi" }] }, { "index": "9ff6b3d5-8e85-4dfd-ad94-bd2dd274f40c", "items": [{ "id": "WXHvy" }] }, { "index": "36adc0f7-d522-4112-8c10-e854c634865c", "items": [{ "id": "vMOPC" }] }, { "index": "845a027e-37be-4efa-9228-0c8f4d764cba", "items": [{ "id": "ordIJ" }] }, { "index": "d0ca3158-474b-429f-9a73-508694b9729a", "items": [{ "id": "GSkBV" }] }, { "index": "0b7835a5-917a-4381-8af9-565366e1b8a0", "items": [{ "id": "PjqtN" }] }, { "index": "ecf013f0-e73f-4eba-8a01-79b25fdb6729", "items": [{ "id": "FMqWR" }] }, { "index": "6b813733-0eb8-4fe5-b21b-8bbc86e72504", "items": [{ "id": "IbamH" }] }, { "index": "f299934a-0533-45d7-90a8-2d792f060933", "items": [{ "id": "WhKPL" }] }, { "index": "6a71e4f8-e5b5-4508-9491-0c59e16e170e", "items": [{ "id": "JxmeM" }] }, { "index": "b720d0f7-971d-44f5-920f-8a9b1924210b", "items": [{ "id": "qCTBc" }] }, { "index": "cf945005-2e28-4041-8b85-dfb2aaa99e97", "items": [{ "id": "rWBOd" }] }, { "index": "cac3265e-640d-48df-b5bc-99e59cb507fc", "items": [{ "id": "ifsDe" }] }], "backendScript": "", "payload": "{}" }, "information": ["KypTX", "KyRfG", "ljpQJ", "xbkST", "vnnJs", "dwRzG", "GfmPi", "WXHvy", "vMOPC", "ordIJ", "GSkBV", "PjqtN", "FMqWR", "IbamH", "WhKPL", "JxmeM", "qCTBc", "rWBOd", "ifsDe"] }, { "id": "57cb05a7-0282-4161-8e4f-a7c6be239b55", "name": "To-Progress", "design": { "rows": [{ "index": "750ae4ce-ee15-4d38-b184-d14201b754a0", "items": [{ "id": "KypTX" }] }, { "index": "ec2ccad3-c29d-44cc-b032-033befb91828", "items": [{ "id": "KyRfG" }] }, { "index": "4c71a8ea-a384-4b22-9e40-3af45da26e3d", "items": [{ "id": "ljpQJ" }] }, { "index": "ed689197-71de-4cec-91e9-19b8908c196e", "items": [{ "id": "qCTBc" }] }, { "index": "158d0996-a265-4a3e-8199-5f820c330402", "items": [{ "id": "mHXsA" }] }], "backendScript": "", "payload": "{}" }, "information": ["KypTX", "KyRfG", "ljpQJ", "qCTBc", "mHXsA"] }, { "id": "c2d747bd-6030-4fff-bd60-9b31d43649b3", "name": "To-Done", "information": [] }],
      action: 'edit',
      search: '',
    };
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";


.listing-designer-dropdown {
  width: 400px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: 'Arial', sans-serif;
  color: #333;
}

.top-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.editable-text {
  flex-grow: 1;
  border-radius: 4px;
  font-size: 14px;
  font-weight: bold;
  border: none
}

.section .title {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #555;
}

.card-section-wrapper {
  width: 100%;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 0.4rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f9f9f9;
}

.card-column {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin-bottom: 0.5rem;
}

.card-row {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 0.3rem 0.5rem;
  font-size: 0.85rem;
}

.card-row.default-card {
  height: 50px;
  background-color: #f4f4f4;
  align-items: center;
  padding-right: 20px;
}

.row-cell {
  flex: 1;
  padding: 0 0.25rem;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-cell {
  border: 1px solid var(--fts-primary);
  padding: 4px 5px;
  border-radius: 4px;
  width: fit-content;
}

.row-cell.align-center {
  text-align: center;
}

.row-cell.align-left {
  text-align: left;
}

.row-cell.align-right {
  text-align: right;
}

.drag-icon {
  flex: 0 0 auto;
  cursor: grab;
  color: #999;
  padding-right: 0.5rem;
}

.default-text {
  flex: 1;
  text-align: center;
  color: #666;
}

.add-row-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
}

.add-row-button {
  padding: 0.2rem 0.6rem;
  font-size: 0.9rem;
  border-radius: 9999px;
  border: 1px solid #ccc;
  cursor: pointer;
  color: var(--fts-text);
}

.settings-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border: 1px solid #eee;
  border-radius: 6px;
  background-color: #fafafa;
  font-size: 0.9rem;
}

.edit-button {
  background: none;
  border: none;
  color: var(--fts-form-label);
  cursor: pointer;
  padding: 0;
}
</style>
